<!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<!-- eslint-disable vuejs-accessibility/mouse-events-have-key-events -->
<!-- mouse handling differs from key handling. both will be used -->
<template>
  <div class="mmm-sub-nav-desktop" :class="['mmm-sub-nav-desktop--' + item.accent]">
    <div class="mmm-sub-nav-desktop__layout mmm-sub-nav-desktop-layout">
      <div class="mmm-sub-nav-desktop-layout__teaser" v-if="item.navTeaserSelector">
        <NavTeaser :navTeaserSelector="item.navTeaserSelector" ref="teaser" />
      </div>
      <div
        class="mmm-sub-nav-desktop-layout__navigation mmm-sub-nav-desktop-navigation"
        @mouseleave="closeSubMenu()"
      >
        <div
          class="mmm-sub-nav-desktop-navigation__list"
          v-if="item.children.length > 0"
          role="menu"
          :id="`submenu-${index}`"
          aria-label="Untermenu von {{ item.title }}"
        >
          <NavSubMenuCategoryDesktop
            @update-selection="handleUpdateSelection($event)"
            @update-selection-without-focus="handleUpdateSelection($event, true)"
            :path="[...props.path, index + 1]"
            :is-keyboard-active="isKeyboardActive"
            :subMenuSelection="subMenuSelection"
            :depth="0"
            class="mmm-sub-nav-desktop-navigation__item"
            v-for="(cat, index) in item.children"
            :key="cat.path"
            :item="cat"
            :index="index"
            :ref="
              el => {
                if (el && typeof el === 'object' && 'handleKeyLeft' in el) {
                  categories[index] = el as unknown as INavSubMenuCategoryDesktopComponent;
                } else {
                  categories[index] = null;
                }
              }
            "
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue';

import { type ISubHeaderNavItem } from '../helper';
import NavTeaser, { type INavTeaserComponent } from '../shared/NavTeaser.ce.vue';

import NavSubMenuCategoryDesktop, {
  type INavSubMenuCategoryDesktopComponent,
} from './NavSubMenuCategoryDesktop.ce.vue';

const categories = ref<(INavSubMenuCategoryDesktopComponent | null)[]>([]);
const teaser = ref<INavTeaserComponent | null>();
let currentIndex = 0;

const props = defineProps<{
  item: ISubHeaderNavItem;
  index: number;
  subMenuSelection: number[];
  path: number[];
  isKeyboardActive: boolean;
}>();

function handleKeyLeft() {
  if (props.subMenuSelection.length === 2) {
    currentIndex--;
    if (currentIndex < 0) {
      currentIndex = categories.value.length;
    }
    focusCurrentIndex();
  } else {
    categories.value[currentIndex - 1]?.handleKeyLeft();
  }
}

function handleKeyRight() {
  if (props.subMenuSelection.length === 2) {
    currentIndex++;
    if (currentIndex > categories.value.length) {
      currentIndex = 0;
    }
    focusCurrentIndex();
  } else {
    categories.value[currentIndex - 1]?.handleKeyRight();
  }
}

function handleKeyUp() {
  if (props.subMenuSelection.length === 2) {
    const temp = [...props.subMenuSelection].slice(0, -1);
    emit('updateSelection', temp);
  } else {
    categories.value[currentIndex - 1]?.handleKeyUp();
  }
}

function handleKeyDown() {
  if (props.subMenuSelection.length === 1) {
    const tmp = [...props.subMenuSelection, 0];
    focusCurrentIndex(tmp);
  } else if (
    props.subMenuSelection.length === 2 &&
    props.item.children[currentIndex - 1]?.children.length === 0
  ) {
    currentIndex++;
    if (currentIndex > categories.value.length) {
      currentIndex = 0;
    }
    focusCurrentIndex();
  } else {
    categories.value[currentIndex - 1]?.handleKeyDown();
  }
}

function handleEscape() {
  if (props.subMenuSelection.length === 2) {
    const temp = [...props.subMenuSelection].slice(0, -1);
    emit('updateSelection', temp);
  } else {
    categories.value[currentIndex - 1]?.handleEscape();
  }
}

function focusCurrentIndex(currentMenuSelection?: number[]) {
  const cat = categories.value[currentIndex - 1];
  if (cat) {
    cat.focus();
  } else if (currentIndex === 0) {
    if (teaser.value) {
      teaser.value.focusButton();
    }
  }
  let temp = [];
  if (currentMenuSelection) {
    temp = currentMenuSelection;
  } else {
    temp = [...props.subMenuSelection].slice(0, -1);
    temp.push(currentIndex);
  }
  emit('updateSelection', temp);
}

function handleUpdateSelection(selection: number[], ignoreFocus: boolean = false) {
  emit('updateSelection', selection, ignoreFocus);
  if (selection.length === 2) {
    const index = selection[1] - 1;
    if (!ignoreFocus) {
      categories.value[index]?.focus();
    }
  }
}

function closeSubMenu() {
  const tmp = [...props.subMenuSelection];
  tmp.splice(3, Infinity);
  emit('updateSelection', tmp, true);
}

const emit = defineEmits(['updateSelection']);

defineExpose({
  handleKeyLeft,
  handleKeyRight,
  handleKeyUp,
  handleKeyDown,
  handleEscape,
});

export interface INavSubMenuDesktopComponent {
  handleKeyLeft(): void;
  handleKeyRight(): void;
  handleKeyUp(): void;
  handleKeyDown(): void;
  handleEscape(): void;
}
</script>

<style lang="scss" scoped>
.mmm-sub-nav-desktop {
  position: absolute;
  display: flex;
  flex-direction: column;
  width: 100%;
  left: 0;
  min-height: var(--default-min-height);
  height: auto;
  background: var(--overlay-background);
  border: 8px solid var(--overlay-background);
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.05);
  box-sizing: border-box;

  &--first {
    --current-accent: var(--first-accent);
  }

  &--second {
    --current-accent: var(--second-accent);
  }

  &--third {
    --current-accent: var(--third-accent);
  }

  &--forth {
    --current-accent: var(--forth-accent);
  }

  &__layout {
    display: flex;
    max-width: 1490px;
    width: 100%;
    margin: auto;
    align-items: center;
    padding: 0 59px;
    box-sizing: border-box;
    height: 100%;
  }
}

.mmm-sub-nav-desktop-layout {
  display: grid;
  grid-template-columns: minmax(0, 450px) 1fr;
  gap: 24px;
  align-items: start;
  height: 100%;
  flex: 1 1 100%;

  &__teaser {
    width: 100%;
    padding: 40px 0;
  }

  &__navigation {
    width: 100%;
    padding: 40px 0;
  }
}

.mmm-sub-nav-desktop-navigation {
  position: relative;
  overflow: hidden;
  height: 100%;
  box-sizing: border-box;
  translate: -3px 0;

  &__list {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    list-style: none;
    padding: 0;
    margin: 0;
  }
}
</style>
